import React, { useCallback, useState } from 'react';
import { Upload, FileJson, AlertCircle } from 'lucide-react';
import { parseZapierExport, validateZapierExport } from '../utils/zapierParser';
import { useStore } from '../store/useStore';
import { LoadingSpinner } from './LoadingSpinner';
import { getUserErrorMessage } from '../utils/errors';

export const DropZone: React.FC = () => {
  const [isDragActive, setIsDragActive] = useState(false);
  const { setWorkflows, setFileName, setError, error, isLoading, loadingProgress, loadingMessage, setLoadingState } = useStore();

  const processFile = useCallback(
    async (file: File) => {
      setLoadingState(true, 0, 'Starting file upload...');
      setError(null);

      try {
        // Stage 1: Read file (0-30%)
        setLoadingState(true, 10, 'Reading file...');

        // CRITICAL: Use FileReader API - NO server upload
        const text = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onprogress = (e) => {
            if (e.lengthComputable) {
              const percentLoaded = Math.floor((e.loaded / e.total) * 30);
              setLoadingState(true, percentLoaded, 'Reading file...');
            }
          };
          reader.onload = (e) => {
            const result = e.target?.result;
            if (typeof result === 'string') {
              resolve(result);
            } else {
              reject(new Error('Failed to read file as text'));
            }
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsText(file);
        });

        // Stage 2: Parse JSON (30-60%)
        setLoadingState(true, 35, 'Parsing JSON...');
        let json;
        try {
          json = JSON.parse(text);
          setLoadingState(true, 50, 'JSON parsed successfully');
        } catch (parseError) {
          setError('Invalid JSON file. Please ensure the file is a valid Zapier export.');
          setLoadingState(false);
          return;
        }

        // Stage 3: Validate (60-80%)
        setLoadingState(true, 60, 'Validating Zapier export format...');
        const validation = validateZapierExport(json);
        if (!validation.isValid) {
          setError(validation.error || 'Invalid Zapier export format');
          setLoadingState(false);
          return;
        }

        // Stage 4: Parse workflows (80-100%)
        setLoadingState(true, 75, 'Processing workflows...');
        const workflows = parseZapierExport(json);

        if (workflows.length === 0) {
          setError('No workflows found in the file');
          setLoadingState(false);
          return;
        }

        setLoadingState(true, 90, `Found ${workflows.length} workflow${workflows.length !== 1 ? 's' : ''}...`);

        // Success!
        setWorkflows(workflows);
        setFileName(file.name);
        setError(null);

        setLoadingState(true, 100, 'Complete!');
        // Clear loading state after brief delay to show completion
        setTimeout(() => setLoadingState(false), 300);
      } catch (error) {
        console.error('Error processing file:', error);
        setError(getUserErrorMessage(error));
        setLoadingState(false);
      }
    },
    [setWorkflows, setFileName, setError, setLoadingState]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const files = Array.from(e.dataTransfer.files);
      const jsonFile = files.find((f) => f.name.endsWith('.json'));

      if (jsonFile) {
        processFile(jsonFile);
      } else {
        setError('Please drop a .json file');
      }
    },
    [processFile, setError]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile]
  );

  return (
    <div className="w-full max-w-2xl mx-auto p-8 animate-fade-in">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-xl p-12 text-center
          transition-all duration-200 ease-in-out
          ${
            isDragActive
              ? 'border-primary bg-primary/5 scale-105'
              : 'border-border bg-card hover:border-primary/50 hover:bg-accent/50'
          }
          ${isLoading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
        `}
      >
        <input
          type="file"
          accept=".json"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isLoading}
        />

        <div className="flex flex-col items-center gap-4">
          {isLoading ? (
            <LoadingSpinner
              size="lg"
              message={loadingMessage}
              progress={loadingProgress}
            />
          ) : (
            <>
              {isDragActive ? (
                <FileJson className="w-16 h-16 text-primary animate-bounce" />
              ) : (
                <Upload className="w-16 h-16 text-muted-foreground" />
              )}
              <div>
                <h3 className="text-xl font-semibold mb-2">
                  {isDragActive ? 'Drop your file here' : 'Drop Zapier JSON export'}
                </h3>
                <p className="text-muted-foreground">
                  or click to browse for a .json file
                </p>
              </div>
              <div className="mt-4 px-4 py-2 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Your data never leaves your browser
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3 animate-fade-in">
          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-destructive">Error</p>
            <p className="text-sm text-destructive/80 mt-1">{error}</p>
          </div>
        </div>
      )}

      <div className="mt-8 text-center text-sm text-muted-foreground">
        <p>
          Supported formats: Zapier GDPR export, single workflow export, or custom JSON
          format
        </p>
      </div>
    </div>
  );
};
